import type {
  CampaignChapterDefinition,
  CampaignScenarioDefinition,
  DifficultyDefinition,
  DifficultyId,
} from "../domain/types";
import { MAPS } from "./map";

export const DIFFICULTIES: Readonly<Record<DifficultyId, DifficultyDefinition>> = Object.freeze({
  easy: {
    id: "easy",
    enemyHealthMultiplier: 1,
    enemySpeedMultiplier: 1,
    rewardMultiplier: 0.3,
    startingCurrencyMultiplier: 1.4,
    livesMultiplier: 1.35,
    statusResistanceBonus: 0,
    waveCount: 50,
  },
  medium: {
    id: "medium",
    enemyHealthMultiplier: 1.5,
    enemySpeedMultiplier: 1.06,
    rewardMultiplier: 0.22,
    startingCurrencyMultiplier: 1.05,
    livesMultiplier: 0.85,
    statusResistanceBonus: 0.06,
    waveCount: 75,
  },
  hard: {
    id: "hard",
    enemyHealthMultiplier: 2.15,
    enemySpeedMultiplier: 1.12,
    rewardMultiplier: 0.16,
    startingCurrencyMultiplier: 0.78,
    livesMultiplier: 0.55,
    statusResistanceBonus: 0.14,
    waveCount: 100,
  },
});

export const CHAPTERS: readonly CampaignChapterDefinition[] = [
  {
    id: "outer-realm",
    displayName: "I — The Outer Realm",
    mapIds: ["castle-approach", "royal-courtyard"],
  },
  {
    id: "warfront",
    displayName: "II — The Warfront",
    mapIds: ["siege-grounds", "enchanted-forest"],
  },
  {
    id: "border-keeps",
    displayName: "III — The Border Keeps",
    mapIds: ["frozen-keep", "coastal-citadel"],
  },
  {
    id: "deep-kingdom",
    displayName: "IV — The Deep Kingdom",
    mapIds: ["underground-stronghold", "volcanic-fortress"],
  },
];

const modes: readonly DifficultyId[] = ["easy", "medium", "hard"];

export const SCENARIOS: readonly CampaignScenarioDefinition[] = Object.values(MAPS).flatMap(
  (map, mapIndex) =>
    modes.map((difficultyId, difficultyIndex) => {
      const difficulty = DIFFICULTIES[difficultyId];
      return {
        id: `${map.id}:${difficultyId}`,
        mapId: map.id,
        displayName:
          difficultyId === "easy" ? "Easy" : difficultyId === "medium" ? "Medium" : "Hard",
        difficulty: difficultyId,
        startingCurrency: Math.round(map.startingCurrency * difficulty.startingCurrencyMultiplier),
        startingLives: Math.max(8, Math.round(map.startingLives * difficulty.livesMultiplier)),
        waveSetId: `${map.id}-${difficultyId}`,
        loadoutLimit: 5,
        unlockRequirement:
          mapIndex === 0 ? "default" : `${Object.values(MAPS)[mapIndex - 1]!.id}:easy`,
        rewards: {
          stars: difficultyIndex + 1,
          royalSeals: 20 + mapIndex * 8 + difficultyIndex * 10,
        },
        optionalObjectives: [
          "Lose no lives",
          "Complete without selling",
          "Spend below the treasury target",
        ],
      };
    }),
);

export function scenarioById(id: string | undefined): CampaignScenarioDefinition | undefined {
  return id ? SCENARIOS.find((scenario) => scenario.id === id) : undefined;
}
